import React, { useContext, useState, useEffect } from "react";
import AdminNavBar from "@/components/AdminNavBar";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Database, AlertTriangle, Trash2, Search, CheckCircle, Download, Upload, RefreshCw, Archive, FileCheck, BookOpen, Users, ArrowUpDown, Key, Eye, EyeOff, Activity, Mail, Send, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import DatabaseAdminGuide from "@/components/DatabaseAdminGuide";
import TestAccountsGuide from "@/components/TestAccountsGuide";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUtilities() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [orphanedPlayers, setOrphanedPlayers] = useState(null);
  const [orphanedRounds, setOrphanedRounds] = useState(null);
  const [duplicateRounds, setDuplicateRounds] = useState(null);
  const [integrityResults, setIntegrityResults] = useState(null);
  const [competitionsData, setCompetitionsData] = useState(null);
  const [allUsers, setAllUsers] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showTestAccountsGuide, setShowTestAccountsGuide] = useState(false);
  
  // Password change
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Test accounts
  const [testAccounts, setTestAccounts] = useState([]);
  const [loadingTestAccounts, setLoadingTestAccounts] = useState(false);
  const [resettingTestAccounts, setResettingTestAccounts] = useState(false);
  const [showCreateTestDialog, setShowCreateTestDialog] = useState(false);
  const [showEditTestDialog, setShowEditTestDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [testAccountName, setTestAccountName] = useState("");
  const [testAccountEmail, setTestAccountEmail] = useState("");
  const [testAccountPassword, setTestAccountPassword] = useState("");
  const [testAccountIsAdmin, setTestAccountIsAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [creatingTestAccount, setCreatingTestAccount] = useState(false);
  const [updatingTestAccount, setUpdatingTestAccount] = useState(false);
  const [deletingTestData, setDeletingTestData] = useState(false);

  // Email Activity Log
  const [emailLog, setEmailLog] = useState(null);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  const [emailFilterStatus, setEmailFilterStatus] = useState("");
  const [emailFilterCategory, setEmailFilterCategory] = useState("");
  const [emailFilterQuery, setEmailFilterQuery] = useState("");
  const [showEmailDetail, setShowEmailDetail] = useState(null);
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Auto-load test accounts on component mount
  useEffect(() => {
    fetchTestAccounts();
    fetchHealth();
    fetchEmailLog();
  }, []);

  const fetchEmailLog = async () => {
    setEmailLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailFilterStatus) params.append("status", emailFilterStatus);
      if (emailFilterCategory) params.append("category", emailFilterCategory);
      if (emailFilterQuery.trim()) params.append("q", emailFilterQuery.trim());
      params.append("limit", "100");
      const res = await axios.get(`${API}/admin/utilities/emails?${params.toString()}`, {
        withCredentials: true,
      });
      setEmailLog(res.data);
    } catch (err) {
      toast.error("Failed to load email log");
    } finally {
      setEmailLogLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient.trim() || !testEmailRecipient.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSendingTestEmail(true);
    try {
      await axios.post(
        `${API}/admin/utilities/emails/test`,
        { recipient: testEmailRecipient.trim() },
        { withCredentials: true }
      );
      toast.success(`Test email sent to ${testEmailRecipient.trim()}`);
      setShowTestEmailDialog(false);
      setTestEmailRecipient("");
      fetchEmailLog();
      fetchHealth();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Test email failed";
      toast.error(detail);
      fetchEmailLog();
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleResendEmail = async (entry) => {
    const to = (entry.recipients || []).join(", ");
    if (!window.confirm(`Resend "${entry.subject}" to ${to}?\n\nNote: original attachments are NOT resent.`)) {
      return;
    }
    setResendingId(entry.id);
    try {
      await axios.post(
        `${API}/admin/utilities/emails/${entry.id}/resend`,
        {},
        { withCredentials: true }
      );
      toast.success("Email resent");
      fetchEmailLog();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Resend failed";
      toast.error(detail);
      fetchEmailLog();
    } finally {
      setResendingId(null);
    }
  };
  
  // Real-time system health banner. Cheap enough to poll on demand;
  // refresh button + auto-refresh after any mutation from this page.
  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/utilities/health`, { withCredentials: true });
      setHealth(res.data);
    } catch (err) {
      // Silent — banner just doesn't render
    } finally {
      setHealthLoading(false);
    }
  };

  if (!user || user.role !== "admin") {
    navigate("/");
    return null;
  }

  // Orphaned Players
  const scanOrphanedPlayers = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/scan-orphaned-players`, {
        withCredentials: true
      });
      setOrphanedPlayers(response.data);
      if (response.data.orphaned_players.length === 0) {
        toast.success("No orphaned player records found. Database is clean!");
      } else {
        toast.warning(`Found ${response.data.orphaned_players.length} orphaned player record(s)`);
      }
    } catch (error) {
      toast.error("Failed to scan database");
    } finally {
      setScanning(false);
    }
  };

  const cleanOrphanedPlayers = async () => {
    if (!orphanedPlayers || orphanedPlayers.orphaned_players.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete ${orphanedPlayers.orphaned_players.length} orphaned player record(s).\n\n` +
      `This action cannot be undone.\n\n` +
      `Are you absolutely sure you want to proceed?`
    );

    if (!confirmed) return;

    setCleaning(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/clean-orphaned-players`,
        { player_ids: orphanedPlayers.orphaned_players.map(p => p.player_id) },
        { withCredentials: true }
      );
      toast.success(`Successfully cleaned ${response.data.deleted_count} orphaned record(s)`);
      setOrphanedPlayers(null);
    } catch (error) {
      toast.error("Failed to clean orphaned records");
    } finally {
      setCleaning(false);
    }
  };


  // Orphaned Rounds
  const scanOrphanedRounds = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/scan-orphaned-rounds`, {
        withCredentials: true
      });
      setOrphanedRounds(response.data);
      if (response.data.orphaned_rounds.length === 0) {
        toast.success("No orphaned round records found. Database is clean!");
      } else {
        toast.warning(`Found ${response.data.orphaned_rounds.length} orphaned round record(s)`);
      }
    } catch (error) {
      toast.error("Failed to scan database");
    } finally {
      setScanning(false);
    }
  };

  const cleanOrphanedRounds = async () => {
    if (!orphanedRounds || orphanedRounds.orphaned_rounds.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete ${orphanedRounds.orphaned_rounds.length} orphaned round record(s).\n\n` +
      `These are rounds that belong to players that no longer exist in the database.\n\n` +
      `This action cannot be undone.\n\n` +
      `Are you absolutely sure you want to proceed?`
    );

    if (!confirmed) return;

    setCleaning(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/clean-orphaned-rounds`,
        { round_ids: orphanedRounds.orphaned_rounds.map(r => r.round_id) },
        { withCredentials: true }
      );
      toast.success(`Successfully cleaned ${response.data.deleted_count} orphaned round(s)`);
      setOrphanedRounds(null);
      // Re-run integrity check to update results
      runIntegrityCheck();
    } catch (error) {
      toast.error("Failed to clean orphaned rounds");
    } finally {
      setCleaning(false);
    }
  };

  // Duplicate Rounds
  const scanDuplicateRounds = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/scan-duplicate-rounds`, {
        withCredentials: true
      });
      setDuplicateRounds(response.data);
      if (response.data.duplicates.length === 0) {
        toast.success("No duplicate rounds found!");
      } else {
        toast.warning(`Found ${response.data.duplicates.length} duplicate group(s)`);
      }
    } catch (error) {
      toast.error("Failed to scan for duplicates");
    } finally {
      setScanning(false);
    }
  };

  const cleanDuplicateRounds = async () => {
    if (!duplicateRounds || duplicateRounds.duplicates.length === 0) {
      return;
    }

    const allRoundsToDelete = duplicateRounds.duplicates.flatMap(d => d.rounds_to_delete);
    
    const confirmed = window.confirm(
      `⚠️ WARNING: This will delete ${allRoundsToDelete.length} duplicate round(s).\n\n` +
      `The most recent round in each group will be kept.\n\n` +
      `This action cannot be undone.\n\n` +
      `Are you sure you want to proceed?`
    );

    if (!confirmed) return;

    setCleaning(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/clean-duplicate-rounds`,
        { round_ids: allRoundsToDelete },
        { withCredentials: true }
      );
      toast.success(`Successfully deleted ${response.data.deleted_count} duplicate round(s)`);
      setDuplicateRounds(null);
    } catch (error) {
      toast.error("Failed to clean duplicate rounds");
    } finally {
      setCleaning(false);
    }
  };

  // Backup Database
  const backupDatabase = async () => {
    const confirmed = window.confirm(
      "This will create a complete backup of your database and download it as a JSON file.\n\n" +
      "This is a safe operation and will not modify any data.\n\n" +
      "Proceed with backup?"
    );

    if (!confirmed) return;

    setBackingUp(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/backup-database`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0].replace(/-/g, '');
      link.setAttribute('download', `golf_eclectic_backup_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Database backup downloaded successfully!");
    } catch (error) {
      toast.error("Failed to create backup");
    } finally {
      setBackingUp(false);
    }
  };

  // Restore Database
  const handleRestoreDatabase = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const confirmed = window.confirm(
      "⚠️ CRITICAL WARNING ⚠️\n\n" +
      "This will COMPLETELY REPLACE all data in your database with the backup file.\n\n" +
      "ALL CURRENT DATA WILL BE DELETED INCLUDING:\n" +
      "- All users and accounts\n" +
      "- All competitions\n" +
      "- All scores and rounds\n" +
      "- All messages\n" +
      "- Everything will be replaced with the backup data\n\n" +
      "This action CANNOT BE UNDONE!\n\n" +
      "RECOMMENDATION: Download a current backup first.\n\n" +
      "Are you ABSOLUTELY SURE you want to proceed with restore?"
    );

    if (!confirmed) {
      event.target.value = ''; // Reset file input
      return;
    }

    restoreDatabase(file);
    event.target.value = ''; // Reset file input after processing
  };

  const restoreDatabase = async (file) => {
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('backup_file', file);

      const response = await axios.post(`${API}/admin/utilities/restore-database`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      const stats = response.data.restore_stats;
      let statsMessage = '\n\nRestore Statistics:\n';
      for (const [collection, stat] of Object.entries(stats)) {
        statsMessage += `- ${collection}: Deleted ${stat.deleted}, Inserted ${stat.inserted}\n`;
      }

      toast.success(`Database restored successfully!${statsMessage}`, {
        duration: 8000
      });

      // Refresh the page after a short delay to reflect restored data
      setTimeout(() => {
        window.location.reload();
      }, 3000);

    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to restore database";
      toast.error(`Restore failed: ${errorMsg}`);
    } finally {
      setRestoring(false);
    }
  };

  // Recalculate Best Scores
  const recalculateBestScores = async () => {
    const confirmed = window.confirm(
      "⚠️ This will recalculate ALL best scores from scratch.\n\n" +
      "This operation may take several minutes for large databases.\n\n" +
      "Best scores will be rebuilt by analyzing all rounds.\n\n" +
      "Proceed with recalculation?"
    );

    if (!confirmed) return;

    setRecalculating(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/recalculate-best-scores`,
        {},
        { withCredentials: true }
      );
      toast.success(`Recalculated ${response.data.recalculated_count} player record(s)!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred during recalculation`);
      }
    } catch (error) {
      toast.error("Failed to recalculate best scores");
    } finally {
      setRecalculating(false);
    }
  };

  // Archive/Unarchive Competitions
  const scanCompetitions = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/scan-archived-competitions`, {
        withCredentials: true
      });
      setCompetitionsData(response.data);
      toast.success(`Found ${response.data.active_count} active and ${response.data.archived_count} archived competition(s)`);
    } catch (error) {
      toast.error("Failed to scan competitions");
    } finally {
      setScanning(false);
    }
  };

  const archiveCompetitions = async (competitionIds) => {
    if (competitionIds.length === 0) return;

    const confirmed = window.confirm(
      `Archive ${competitionIds.length} competition(s)?\n\n` +
      `Archived competitions will be hidden from active lists but data is preserved.\n\n` +
      `You can unarchive them later if needed.`
    );

    if (!confirmed) return;

    setArchiving(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/archive-competitions`,
        { competition_ids: competitionIds },
        { withCredentials: true }
      );
      toast.success(`Archived ${response.data.archived_count} competition(s)`);
      scanCompetitions(); // Refresh list
    } catch (error) {
      toast.error("Failed to archive competitions");
    } finally {
      setArchiving(false);
    }
  };

  const unarchiveCompetitions = async (competitionIds) => {
    if (competitionIds.length === 0) return;

    const confirmed = window.confirm(
      `Unarchive ${competitionIds.length} competition(s)?\n\n` +
      `These competitions will appear in active lists again.`
    );

    if (!confirmed) return;

    setArchiving(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/unarchive-competitions`,
        { competition_ids: competitionIds },
        { withCredentials: true }
      );
      toast.success(`Unarchived ${response.data.unarchived_count} competition(s)`);
      scanCompetitions(); // Refresh list
    } catch (error) {
      toast.error("Failed to unarchive competitions");
    } finally {
      setArchiving(false);
    }
  };

  // Integrity Check
  const runIntegrityCheck = async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/integrity-check`, {
        withCredentials: true
      });
      setIntegrityResults(response.data);
      if (response.data.total_issues === 0) {
        toast.success("✅ Database integrity check passed! No issues found.");
      } else {
        toast.warning(`⚠️ Found ${response.data.total_issues} issue(s) in database`);
      }
    } catch (error) {
      toast.error("Failed to run integrity check");
    } finally {
      setScanning(false);
    }
  };

  // User Management
  const loadAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/utilities/list-all-users`, {
        withCredentials: true
      });
      setAllUsers(response.data);
      toast.success(`Loaded ${response.data.total_count} user(s)`);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const deleteUserCompletely = async (userId, userEmail) => {
    const confirmed = window.confirm(
      `⚠️ CRITICAL WARNING: Delete user permanently?\n\n` +
      `User: ${userEmail}\n\n` +
      `This will DELETE:\n` +
      `• User account\n` +
      `• All player records (from all competitions)\n` +
      `• All rounds and scores\n` +
      `• All sessions and preferences\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Type 'DELETE' in the next prompt to confirm.`
    );

    if (!confirmed) return;

    const confirmation = prompt(`Type DELETE to confirm deletion of ${userEmail}:`);
    if (confirmation !== 'DELETE') {
      toast.error("Deletion cancelled - confirmation text did not match");
      return;
    }

    setDeletingUser(true);
    try {
      const response = await axios.post(
        `${API}/admin/utilities/delete-user`,
        { user_id: userId },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      loadAllUsers(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUser(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/admin/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        { withCredentials: true }
      );
      
      toast.success("Password changed successfully!");
      setShowPasswordDialog(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Test account functions
  const fetchTestAccounts = async () => {
    setLoadingTestAccounts(true);
    try {
      const response = await axios.get(`${API}/admin/test-accounts`, {
        withCredentials: true
      });
      setTestAccounts(response.data);
    } catch (error) {
      toast.error("Failed to load test accounts");
    } finally {
      setLoadingTestAccounts(false);
    }
  };

  const handleCreateTestAccount = async () => {
    if (!testAccountEmail || !testAccountName || !testAccountPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (testAccountPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setCreatingTestAccount(true);
    try {
      await axios.post(
        `${API}/admin/test-accounts/create`,
        {
          name: testAccountName,
          email: testAccountEmail,
          password: testAccountPassword,
          is_admin: testAccountIsAdmin
        },
        { withCredentials: true }
      );

      toast.success(`Test account created: ${testAccountEmail}`);
      setShowCreateTestDialog(false);
      setTestAccountName("");
      setTestAccountEmail("");
      setTestAccountPassword("");
      setTestAccountIsAdmin(false);
      setShowPassword(false);
      fetchTestAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create test account");
    } finally {
      setCreatingTestAccount(false);
    }
  };


  const openEditDialog = (account) => {
    setEditingAccount(account);
    setTestAccountName(account.name);
    setTestAccountEmail(account.email);
    setTestAccountPassword(""); // Leave blank unless user wants to change
    setShowEditPassword(false); // Reset visibility
    setShowEditTestDialog(true);
  };

  const handleUpdateTestAccount = async () => {
    if (!testAccountName || !testAccountEmail) {
      toast.error("Name and email are required");
      return;
    }

    // Only include password if it was changed
    const updateData = {
      name: testAccountName,
      email: testAccountEmail
    };

    if (testAccountPassword) {
      if (testAccountPassword.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
      updateData.password = testAccountPassword;
    }

    setUpdatingTestAccount(true);
    try {
      await axios.put(
        `${API}/admin/test-accounts/${editingAccount.id}/update`,
        updateData,
        { withCredentials: true }
      );
      toast.success("Test account updated successfully");
      setShowEditTestDialog(false);
      setEditingAccount(null);
      setTestAccountName("");
      setTestAccountEmail("");
      setTestAccountPassword("");
      fetchTestAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update test account");
    } finally {
      setUpdatingTestAccount(false);
    }
  };


  const handleResetTestAccount = async (accountId, accountEmail) => {
    if (!window.confirm(`Reset test account ${accountEmail}? This will delete all their data but keep the account.`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/test-accounts/${accountId}/reset`,
        {},
        { withCredentials: true }
      );
      toast.success(`Test account ${accountEmail} reset successfully`);
      fetchTestAccounts();
    } catch (error) {
      toast.error("Failed to reset test account");
    }
  };

  const handleResetAllTestAccounts = async () => {
    if (!window.confirm("Reset ALL test accounts? This will delete all test account data but keep the accounts.")) {
      return;
    }

    setResettingTestAccounts(true);
    try {
      const response = await axios.post(
        `${API}/admin/test-accounts/reset-all`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message);
      fetchTestAccounts();
    } catch (error) {
      toast.error("Failed to reset test accounts");
    } finally {
      setResettingTestAccounts(false);
    }
  };

  const handleDeleteTestAccount = async (accountId, accountEmail) => {
    if (!window.confirm(`PERMANENTLY DELETE test account ${accountEmail}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/test-accounts/${accountId}`,
        { withCredentials: true }
      );
      toast.success(`Test account ${accountEmail} deleted`);
      fetchTestAccounts();
    } catch (error) {
      toast.error("Failed to delete test account");
    }
  };

  const handleBulkDeleteTestData = async () => {
    if (!window.confirm(
      "⚠️ DELETE ALL TEST DATA?\n\n" +
      "This will permanently delete:\n" +
      "• All test competitions\n" +
      "• All test rounds and scores\n" +
      "• All test player registrations\n" +
      "• All test messages\n" +
      "• All test social posts\n\n" +
      "Test accounts will be kept, but all their data will be removed.\n\n" +
      "Are you sure you want to continue?"
    )) {
      return;
    }

    setDeletingTestData(true);
    try {
      const response = await axios.post(
        `${API}/admin/test-data/bulk-delete`,
        {},
        { withCredentials: true }
      );
      
      const counts = response.data.deleted_counts;
      toast.success(
        `✅ Test data deleted successfully!\n\n` +
        `Deleted:\n` +
        `• ${counts.competitions} competitions\n` +
        `• ${counts.rounds} rounds\n` +
        `• ${counts.players} player registrations\n` +
        `• ${counts.messages} messages\n` +
        `• ${counts.social_posts} social posts`
      );
      
      // Refresh test accounts list (they should still exist)
      fetchTestAccounts();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Only test admins can bulk delete test data");
      } else {
        toast.error("Failed to delete test data: " + (error.response?.data?.detail || error.message));
      }
    } finally {
      setDeletingTestData(false);
    }
  };

  return (
    <div className="min-h-screen bg-app p-6">
      <AdminNavBar />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Admin</span>
            </Button>
            <h1 className="text-xl font-semibold text-ink tracking-tight flex items-center">
              <Database className="w-8 h-8 mr-3 text-accent-deep" />
              Database Utilities
            </h1>
          </div>
          <Button
            onClick={() => setShowGuide(true)}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <BookOpen className="w-4 h-4" />
            <span>View Guide</span>
          </Button>
        </div>

        {/* System Health Banner — real-time integrity snapshot at the top
            of every Utilities visit. Green = all clear; amber/red = one or
            more checks need attention. Click Refresh after any cleanup. */}
        {health && (
          <div
            data-testid="system-health-banner"
            className={`mb-6 rounded-xl border p-4 ${
              health.overall === "crit"
                ? "bg-red-50 border-red-200"
                : health.overall === "warn"
                  ? "bg-amber-50 border-amber-200"
                  : "bg-emerald-50 border-emerald-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className={`w-5 h-5 ${
                  health.overall === "crit" ? "text-red-700"
                    : health.overall === "warn" ? "text-amber-700"
                    : "text-emerald-700"
                }`} />
                <span className="font-semibold text-ink">
                  System health — {health.overall === "ok"
                    ? "all clear"
                    : health.overall === "warn"
                      ? "attention needed"
                      : "action required"}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchHealth}
                disabled={healthLoading}
                data-testid="refresh-health-btn"
                className="flex items-center gap-1"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${healthLoading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {health.checks.map((c) => (
                <div
                  key={c.key}
                  data-testid={`health-check-${c.key}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-md text-sm border ${
                    c.status === "crit"
                      ? "bg-white border-red-300 text-red-800"
                      : c.status === "warn"
                        ? "bg-white border-amber-300 text-amber-800"
                        : "bg-white/70 border-emerald-200 text-emerald-800"
                  }`}
                  title={c.reason || ""}
                >
                  <span className="flex items-center gap-1.5">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        c.status === "crit" ? "bg-red-500"
                          : c.status === "warn" ? "bg-amber-500"
                          : "bg-emerald-500"
                      }`}
                    />
                    {c.label}
                  </span>
                  <span className="font-semibold">
                    {c.key === "unique_index" || c.key === "smtp"
                      ? (c.count ? "OK" : "Down")
                      : c.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email Activity Card — audit trail for every SMTP send from the app,
            with filters, an inspector dialog, resend for text-only emails, and
            a "Send test email" utility to verify SMTP end-to-end. */}
        <Card className="mb-6 border-accent/30" data-testid="email-activity-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-xl flex items-center text-ink">
                <Mail className="w-5 h-5 mr-2 text-accent-deep" />
                Email Activity
                {emailLog && (
                  <span className="ml-3 text-sm font-normal text-ink-muted">
                    <span className="text-emerald-700 font-medium">{emailLog.counts.sent} sent/delivered</span>
                    {" · "}
                    <span className={emailLog.counts.failed > 0 ? "text-red-700 font-medium" : "text-ink-muted"}>
                      {emailLog.counts.failed} failed/bounced
                    </span>
                  </span>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTestEmailDialog(true)}
                  data-testid="open-test-email-dialog-btn"
                  className="flex items-center gap-1"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send test email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchEmailLog}
                  disabled={emailLogLoading}
                  data-testid="refresh-email-log-btn"
                  className="flex items-center gap-1"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${emailLogLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-ink-muted mb-3">
              Every email the app sends (welcome, password reset, certificates, broadcasts, access codes) is logged here.
              Click a row to inspect the body. Failed rows can be resent (attachments are not preserved).
            </p>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
              <select
                value={emailFilterStatus}
                onChange={(e) => setEmailFilterStatus(e.target.value)}
                data-testid="email-filter-status"
                className="px-3 py-1.5 text-sm border rounded-md bg-white"
              >
                <option value="">All statuses</option>
                <option value="delivered">Delivered</option>
                <option value="sent">Sent (awaiting confirmation)</option>
                <option value="bounced">Bounced</option>
                <option value="complained">Complained</option>
                <option value="delayed">Delayed</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={emailFilterCategory}
                onChange={(e) => setEmailFilterCategory(e.target.value)}
                data-testid="email-filter-category"
                className="px-3 py-1.5 text-sm border rounded-md bg-white"
              >
                <option value="">All categories</option>
                <option value="welcome">Welcome</option>
                <option value="password_reset">Password reset</option>
                <option value="certificate">Certificate</option>
                <option value="broadcast">Broadcast</option>
                <option value="access_code">Access code</option>
                <option value="test">Test</option>
              </select>
              <Input
                placeholder="Search recipient / subject / error…"
                value={emailFilterQuery}
                onChange={(e) => setEmailFilterQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") fetchEmailLog(); }}
                data-testid="email-filter-query"
                className="flex-1 min-w-[200px]"
              />
              <Button
                size="sm"
                onClick={fetchEmailLog}
                data-testid="apply-email-filters-btn"
                className="bg-accent-deep hover:bg-accent-deep"
              >
                <Search className="w-3.5 h-3.5 mr-1" />
                Apply
              </Button>
            </div>

            {/* Table */}
            {emailLog === null ? (
              <div className="text-sm text-ink-muted py-6 text-center">Loading…</div>
            ) : emailLog.items.length === 0 ? (
              <div className="text-sm text-ink-muted py-6 text-center border rounded-md bg-app">
                No emails match the current filters. Try widening the filters or click <em>Send test email</em> to verify delivery.
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-app border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-ink">When</th>
                      <th className="text-left px-3 py-2 font-semibold text-ink">To</th>
                      <th className="text-left px-3 py-2 font-semibold text-ink">Subject</th>
                      <th className="text-left px-3 py-2 font-semibold text-ink">Category</th>
                      <th className="text-left px-3 py-2 font-semibold text-ink">Status</th>
                      <th className="text-right px-3 py-2 font-semibold text-ink">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLog.items.map((e) => {
                      const ts = new Date(e.timestamp);
                      const tsStr = isNaN(ts.getTime())
                        ? e.timestamp
                        : ts.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
                      const recip = (e.recipients || []).join(", ");
                      return (
                        <tr
                          key={e.id}
                          data-testid={`email-log-row-${e.id}`}
                          className="border-b hover:bg-app/60 cursor-pointer"
                          onClick={() => setShowEmailDetail(e)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-ink-muted">{tsStr}</td>
                          <td className="px-3 py-2 max-w-[220px] truncate text-ink" title={recip}>{recip}</td>
                          <td className="px-3 py-2 max-w-[280px] truncate text-ink" title={e.subject}>{e.subject}</td>
                          <td className="px-3 py-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-app border text-ink-muted">
                              {e.category}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {(() => {
                              const s = (e.status || "").toLowerCase();
                              if (s === "delivered") return (
                                <span className="inline-flex items-center gap-1 text-emerald-800 text-xs font-semibold" title="Confirmed delivered by recipient server">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Delivered
                                </span>
                              );
                              if (s === "sent") return (
                                <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium" title="Accepted by mail provider (awaiting delivery confirmation)">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Sent
                                </span>
                              );
                              if (s === "bounced") return (
                                <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold" title={e.error || "Recipient server rejected"}>
                                  <XCircle className="w-3.5 h-3.5" />
                                  Bounced
                                </span>
                              );
                              if (s === "complained") return (
                                <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold" title="Recipient marked as spam">
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                  Complained
                                </span>
                              );
                              if (s === "delayed") return (
                                <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium" title="Delivery delayed — will retry">
                                  <RefreshCw className="w-3.5 h-3.5" />
                                  Delayed
                                </span>
                              );
                              return (
                                <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium" title={e.error || ""}>
                                  <XCircle className="w-3.5 h-3.5" />
                                  Failed
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(ev) => { ev.stopPropagation(); handleResendEmail(e); }}
                              disabled={resendingId === e.id || !(e.html_content || e.text_content)}
                              data-testid={`resend-email-${e.id}`}
                              title={!(e.html_content || e.text_content) ? "Original body not stored — cannot resend" : "Resend"}
                            >
                              {resendingId === e.id ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-3.5 h-3.5 mr-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {emailLog && emailLog.total > emailLog.items.length && (
              <p className="text-xs text-ink-muted mt-2">
                Showing latest {emailLog.items.length} of {emailLog.total}. Use filters to narrow results.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Warning Banner */}
        <Card className="mb-6 border-warm/40 bg-warm-tint/40">
          <CardHeader>
            <CardTitle className="flex items-center text-warm">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Important: Database Maintenance Tools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-warm">
              <p className="font-semibold">⚠️ Use these tools with caution!</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Always <strong>scan first</strong> to see what will be affected</li>
                <li>These operations <strong>cannot be undone</strong></li>
                <li>Run during <strong>low-traffic periods</strong> if possible</li>
                <li>Consider <strong>backing up</strong> before running cleanup operations</li>
                <li><strong>Read the guide</strong> (click button above) for detailed explanations</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Admin Password Change Card */}
        <Card className="mb-6 border-accent/30 bg-accent-tint-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center text-accent-deep">
              <CheckCircle className="w-5 h-5 mr-2" />
              Admin Account Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-ink-muted mb-4">
              Change your admin password to keep your account secure.
            </p>
            <Button
              onClick={() => setShowPasswordDialog(true)}
              className="bg-accent-deep hover:bg-accent-deep"
            >
              Change Password
            </Button>
          </CardContent>
        </Card>

        {/* Test Account Management Card */}
        <Card className="mb-6 border-accent bg-accent-tint-2">
          <CardHeader>
            <CardTitle className="text-xl flex items-center text-ink">
              <Users className="w-5 h-5 mr-2" />
              🧪 Test Account Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-ink-muted mb-4">
              Create and manage test accounts for testing features in production without affecting real users.
            </p>
            
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                onClick={() => {
                  fetchTestAccounts();
                  setShowCreateTestDialog(false);
                }}
                className="bg-ink hover:bg-ink-hover"
                disabled={loadingTestAccounts}
              >
                {loadingTestAccounts ? "Loading..." : "Manage Test Accounts"}
              </Button>
              
              <Button
                onClick={handleResetAllTestAccounts}
                variant="outline"
                className="border-warm/40 text-warm hover:bg-warm-tint/40"
                disabled={resettingTestAccounts}
              >
                {resettingTestAccounts ? "Resetting..." : "Reset All Test Accounts"}
              </Button>

              <Button
                onClick={handleBulkDeleteTestData}
                variant="outline"
                className="border-red-500 text-red-700 hover:bg-red-50 flex items-center space-x-2"
                disabled={deletingTestData}
              >
                <Trash2 className="w-4 h-4" />
                <span>{deletingTestData ? "Deleting..." : "🧪 Delete All Test Data"}</span>
              </Button>

              <Button
                onClick={() => setShowTestAccountsGuide(true)}
                variant="outline"
                className="flex items-center space-x-2 border-accent text-ink hover:bg-accent-tint-2"
              >
                <BookOpen className="w-4 h-4" />
                <span>View Guide</span>
              </Button>
            </div>

            <div className="mb-4 p-3 bg-accent-tint-2 border border-line rounded text-sm">
              <strong className="text-ink">📝 Standard Test Account Passwords:</strong>
              <ul className="mt-2 space-y-1 ml-4 text-ink">
                <li>• <strong>Test Admin:</strong> <code className="bg-accent-tint px-2 py-0.5 rounded">TestAdmin2025!</code></li>
                <li>• <strong>Test Players:</strong> <code className="bg-accent-tint px-2 py-0.5 rounded">TestPlayer2025!</code></li>
                <li>• <strong>💡 Tip:</strong> Use password visibility toggle (👁️) when creating/editing to avoid typos</li>
              </ul>
            </div>

            <div className="mb-4 p-3 bg-app border border-line rounded text-sm text-accent-deep">
              <strong>💡 Quick Actions:</strong>
              <ul className="mt-1 space-y-1 ml-4">
                <li>• <strong>Reset All:</strong> Clears test data but keeps accounts & passwords</li>
                <li>• <strong>Delete All Test Data:</strong> Removes all test competitions, players, rounds, scores, messages (keeps accounts)</li>
              </ul>
            </div>

            {testAccounts.length > 0 && (
              <div className="mt-4 p-4 bg-white rounded border">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-ink">Test Accounts ({testAccounts.length})</h3>
                  <Button
                    size="sm"
                    onClick={() => setShowCreateTestDialog(true)}
                    className="bg-ink hover:bg-ink-hover"
                  >
                    + Create New
                  </Button>
                </div>
                
                <div className="space-y-2">
                  {testAccounts.map(account => (
                    <div key={account.id} className="flex items-center justify-between p-3 bg-accent-tint-2 rounded border border-line">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🧪</span>
                          <div>
                            <div className="font-semibold text-ink">{account.name}</div>
                            <div className="text-sm text-ink-muted">{account.email}</div>
                          </div>
                        </div>
                        <div className="text-xs text-accent mt-1">
                          Role: {account.role === 'admin' ? '👑 Admin' : '👤 Player'} | Auth: {account.auth_type}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(account)}
                          className="text-accent-deep hover:bg-app"
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResetTestAccount(account.id, account.email)}
                          className="text-warm hover:bg-warm-tint/40"
                        >
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteTestAccount(account.id, account.email)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingTestAccounts && (
              <div className="mt-4 p-4 bg-white rounded border text-center">
                <p className="text-ink-muted">Loading test accounts...</p>
              </div>
            )}

            {testAccounts.length === 0 && !loadingTestAccounts && (
              <div className="mt-4 p-4 bg-white rounded border text-center">
                <p className="text-ink-muted mb-3">No test accounts created yet</p>
                <Button
                  onClick={() => setShowCreateTestDialog(true)}
                  className="bg-ink hover:bg-ink-hover"
                >
                  Create First Test Account
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Backup Database */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Download className="w-5 h-5 mr-2 text-accent-deep" />
                Backup Database
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Create a complete JSON backup of all database collections. Safe operation that only reads data.
              </p>
              <Button
                onClick={backupDatabase}
                disabled={backingUp}
                className="bg-ink hover:bg-blue-700 w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                {backingUp ? "Creating Backup..." : "Download Backup"}
              </Button>
            </CardContent>
          </Card>

          {/* Restore Database */}
          <Card className="border-red-300">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Upload className="w-5 h-5 mr-2 text-red-600" />
                Restore Database
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-sm text-red-800 font-semibold flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  DESTRUCTIVE OPERATION
                </p>
                <p className="text-xs text-red-700 mt-1">
                  This will DELETE all current data and replace it with the backup file. Cannot be undone!
                </p>
              </div>
              <p className="text-sm text-ink-muted mb-4">
                Upload a backup JSON file to completely restore your database. All current data will be replaced.
              </p>
              <div className="space-y-3">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreDatabase}
                  disabled={restoring}
                  className="hidden"
                  id="restore-file-input"
                />
                <label htmlFor="restore-file-input">
                  <Button
                    asChild
                    disabled={restoring}
                    className="bg-red-600 hover:bg-red-700 w-full cursor-pointer"
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {restoring ? "Restoring Database..." : "Select Backup File to Restore"}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-ink-soft text-center">
                  ⚠️ Recommendation: Download a current backup before restoring
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Renumber Rounds by Date - NEW */}
          <Card className="border-accent">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <ArrowUpDown className="w-5 h-5 mr-2 text-accent" />
                Renumber Rounds by Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Renumber all rounds in chronological order by date. Round 1 = earliest date, Round 2 = next date, etc. Safe operation - only updates round numbers.
              </p>
              <Button
                onClick={async () => {
                  if (!window.confirm('This will renumber ALL rounds across ALL competitions by date order. Continue?')) return;
                  try {
                    const response = await axios.post(`${API}/admin/utilities/renumber-all-rounds`, {}, { withCredentials: true });
                    toast.success(response.data.message);
                  } catch (error) {
                    toast.error("Failed to renumber rounds");
                  }
                }}
                className="bg-ink hover:bg-ink-hover w-full"
              >
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Renumber All Rounds
              </Button>
            </CardContent>
          </Card>

          {/* Delete Users */}
          <Card className="border-red-300">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Trash2 className="w-5 h-5 mr-2 text-red-600" />
                Delete Users from System
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Permanently delete users and ALL their data from the system. ⚠️ This removes user accounts, not just competition entries.
              </p>
              {allUsers && (
                <div className="mb-4 max-h-60 overflow-y-auto border rounded p-3 bg-app">
                  {allUsers.users.length === 0 ? (
                    <p className="text-sm text-ink-soft">No users found</p>
                  ) : (
                    <div className="space-y-2">
                      {allUsers.users.filter(u => u.role !== 'admin').map(u => (
                        <div key={u.id} className="flex justify-between items-center p-2 bg-white rounded border">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{u.name}</p>
                            <p className="text-xs text-ink-muted">{u.email}</p>
                            <p className="text-xs text-ink-soft">
                              {u.auth_type === 'google' ? '🔗 Google' : '🔒 Local'} • 
                              {u.competition_count} competition(s)
                            </p>
                          </div>
                          <Button
                            onClick={() => deleteUserCompletely(u.id, u.email)}
                            disabled={deletingUser}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700 text-xs"
                          >
                            Delete
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Button
                onClick={loadAllUsers}
                disabled={loadingUsers}
                variant="outline"
                className="w-full"
              >
                <Search className="w-4 h-4 mr-2" />
                {loadingUsers ? "Loading..." : "Load All Users"}
              </Button>
            </CardContent>
          </Card>

          {/* Orphaned Players */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Trash2 className="w-5 h-5 mr-2 text-red-600" />
                Orphaned Player Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Find and remove player records that reference non-existent user accounts.
              </p>
              {orphanedPlayers && (
                <div className={`mb-4 p-3 rounded border ${orphanedPlayers.orphaned_players.length > 0 ? 'bg-warm-tint/40 border-warm/40' : 'bg-accent-tint-2 border-accent'}`}>
                  <p className="text-sm font-semibold">
                    {orphanedPlayers.orphaned_players.length > 0 
                      ? `Found ${orphanedPlayers.orphaned_players.length} orphaned record(s)`
                      : "✅ No orphaned records found"}
                  </p>
                </div>
              )}
              <div className="flex space-x-2">
                <Button
                  onClick={scanOrphanedPlayers}
                  disabled={scanning}
                  variant="outline"
                  className="flex-1"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {scanning ? "Scanning..." : "Scan"}
                </Button>
                {orphanedPlayers && orphanedPlayers.orphaned_players.length > 0 && (
                  <Button
                    onClick={cleanOrphanedPlayers}
                    disabled={cleaning}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {cleaning ? "Cleaning..." : "Clean"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>


          {/* Orphaned Rounds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Trash2 className="w-5 h-5 mr-2 text-warm" />
                Orphaned Round Records
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Find and remove round records that reference non-existent player records.
              </p>
              {orphanedRounds && (
                <div className={`mb-4 p-3 rounded border ${orphanedRounds.orphaned_rounds.length > 0 ? 'bg-warm-tint/40 border-warm/40' : 'bg-accent-tint-2 border-accent'}`}>
                  <p className="text-sm font-semibold">
                    {orphanedRounds.orphaned_rounds.length > 0 
                      ? `Found ${orphanedRounds.orphaned_rounds.length} orphaned round(s)`
                      : "✅ No orphaned rounds found"}
                  </p>
                  {orphanedRounds.orphaned_rounds.length > 0 && (
                    <div className="mt-2 text-xs bg-white p-2 rounded border max-h-32 overflow-y-auto">
                      <p className="font-semibold text-ink-muted mb-1">Details:</p>
                      {orphanedRounds.orphaned_rounds.map((round, idx) => (
                        <div key={idx} className="text-ink-muted mb-1">
                          • Round from {round.round_date} in "{round.competition_name}" (Player ID: {round.player_id.substring(0, 8)}...)
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex space-x-2">
                <Button
                  onClick={scanOrphanedRounds}
                  disabled={scanning}
                  variant="outline"
                  className="flex-1"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {scanning ? "Scanning..." : "Scan"}
                </Button>
                {orphanedRounds && orphanedRounds.orphaned_rounds.length > 0 && (
                  <Button
                    onClick={cleanOrphanedRounds}
                    disabled={cleaning}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {cleaning ? "Cleaning..." : "Clean"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Duplicate Rounds */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <RefreshCw className="w-5 h-5 mr-2 text-warm" />
                Duplicate Rounds
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Find and remove duplicate round entries (same player, date, competition).
              </p>
              {duplicateRounds && (
                <div className={`mb-4 p-3 rounded border ${duplicateRounds.duplicates.length > 0 ? 'bg-warm-tint/40 border-warm/40' : 'bg-accent-tint-2 border-accent'}`}>
                  <p className="text-sm font-semibold">
                    {duplicateRounds.duplicates.length > 0
                      ? `Found ${duplicateRounds.duplicates.length} duplicate group(s)`
                      : "✅ No duplicates found"}
                  </p>
                </div>
              )}
              <div className="flex space-x-2">
                <Button
                  onClick={scanDuplicateRounds}
                  disabled={scanning}
                  variant="outline"
                  className="flex-1"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {scanning ? "Scanning..." : "Scan"}
                </Button>
                {duplicateRounds && duplicateRounds.duplicates.length > 0 && (
                  <Button
                    onClick={cleanDuplicateRounds}
                    disabled={cleaning}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {cleaning ? "Cleaning..." : "Clean"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recalculate Best Scores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <RefreshCw className="w-5 h-5 mr-2 text-accent" />
                Recalculate Best Scores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Rebuild all best_scores records from scratch by analyzing all rounds. Safe healing operation.
              </p>
              <Button
                onClick={recalculateBestScores}
                disabled={recalculating}
                className="bg-ink hover:bg-ink-hover w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {recalculating ? "Recalculating..." : "Recalculate All"}
              </Button>
            </CardContent>
          </Card>

          {/* Archive/Unarchive Competitions */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <Archive className="w-5 h-5 mr-2 text-accent-deep" />
                Archive/Unarchive Competitions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Archive old competitions to hide them from active lists. Data is preserved and can be unarchived anytime.
              </p>
              
              {competitionsData && (
                <div className="mb-4 space-y-4">
                  {/* Active Competitions */}
                  {competitionsData.active_competitions.length > 0 && (
                    <div className="bg-accent-tint-2 border border-line rounded p-3">
                      <h3 className="font-semibold mb-2 text-accent-deep">
                        Active Competitions ({competitionsData.active_count})
                      </h3>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {competitionsData.active_competitions.map((comp) => (
                          <div key={comp.id} className="bg-white p-2 rounded border flex justify-between items-center">
                            <div className="text-sm">
                              <p className="font-semibold">{comp.name}</p>
                              <p className="text-ink-muted text-xs">
                                {comp.start_date} to {comp.end_date}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => archiveCompetitions([comp.id])}
                              disabled={archiving}
                              className="text-xs"
                            >
                              <Archive className="w-3 h-3 mr-1" />
                              Archive
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Archived Competitions */}
                  {competitionsData.archived_competitions.length > 0 && (
                    <div className="bg-app border border-line-strong rounded p-3">
                      <h3 className="font-semibold mb-2 text-ink">
                        Archived Competitions ({competitionsData.archived_count})
                      </h3>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {competitionsData.archived_competitions.map((comp) => (
                          <div key={comp.id} className="bg-white p-2 rounded border flex justify-between items-center">
                            <div className="text-sm">
                              <p className="font-semibold text-ink-muted">{comp.name}</p>
                              <p className="text-ink-soft text-xs">
                                {comp.start_date} to {comp.end_date}
                              </p>
                              <p className="text-ink-soft text-xs">
                                Archived: {comp.archived_date ? new Date(comp.archived_date).toLocaleDateString('en-GB') : 'N/A'}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => unarchiveCompetitions([comp.id])}
                              disabled={archiving}
                              className="text-xs bg-accent-tint"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Unarchive
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                onClick={scanCompetitions}
                disabled={scanning}
                className="bg-indigo-600 hover:bg-indigo-700 w-full"
              >
                <Search className="w-4 h-4 mr-2" />
                {scanning ? "Scanning..." : competitionsData ? "Refresh List" : "View Competitions"}
              </Button>
            </CardContent>
          </Card>

          {/* Database Integrity Check */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-xl flex items-center">
                <FileCheck className="w-5 h-5 mr-2 text-accent-deep" />
                Database Integrity Check
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-ink-muted mb-4">
                Comprehensive scan for orphaned records, duplicates, invalid data, and inconsistencies. Read-only operation.
              </p>
              
              {integrityResults && (
                <div className={`mb-4 p-4 rounded border ${integrityResults.total_issues > 0 ? 'bg-warm-tint/40 border-warm/40' : 'bg-accent-tint-2 border-accent'}`}>
                  <h3 className="font-semibold mb-3 flex items-center">
                    {integrityResults.total_issues > 0 ? (
                      <AlertTriangle className="w-5 h-5 mr-2 text-warm" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2 text-accent" />
                    )}
                    Integrity Check Results
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white p-2 rounded border">
                      <p className="text-ink-muted">Orphaned Players</p>
                      <p className="text-xl font-bold text-red-600">{integrityResults.summary.orphaned_players}</p>
                      <p className="text-xs text-ink-soft mt-1">Players without user accounts</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-ink-muted">Orphaned Rounds</p>
                      <p className="text-xl font-bold text-warm">{integrityResults.summary.orphaned_rounds}</p>
                      <p className="text-xs text-ink-soft mt-1">Rounds without player records</p>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <p className="text-ink-muted">Duplicate Rounds</p>
                      <p className="text-xl font-bold text-warm">{integrityResults.summary.duplicate_rounds}</p>
                      <p className="text-xs text-ink-soft mt-1">Same player/date/competition</p>
                    </div>
                  </div>
                  {integrityResults.total_issues === 0 && (
                    <p className="text-ink font-semibold mt-3">✅ No issues found. Database is healthy!</p>
                  )}
                  {integrityResults.total_issues > 0 && (
                    <div className="mt-3 bg-white p-3 rounded border border-warm/40">
                      <p className="text-warm font-semibold mb-2">
                        ⚠️ Found {integrityResults.total_issues} total issue(s)
                      </p>
                      <div className="text-sm text-ink-muted space-y-1">
                        {integrityResults.summary.orphaned_players > 0 && (
                          <p>• Use <span className="font-semibold">Orphaned Player Records</span> utility above to fix {integrityResults.summary.orphaned_players} player issue(s)</p>
                        )}
                        {integrityResults.summary.orphaned_rounds > 0 && (
                          <p>• Use <span className="font-semibold">Orphaned Round Records</span> utility above to fix {integrityResults.summary.orphaned_rounds} round issue(s)</p>
                        )}
                        {integrityResults.summary.duplicate_rounds > 0 && (
                          <p>• Use <span className="font-semibold">Duplicate Rounds</span> utility above to fix {integrityResults.summary.duplicate_rounds} duplicate(s)</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <Button
                onClick={runIntegrityCheck}
                disabled={scanning}
                className="bg-cyan-600 hover:bg-cyan-700 w-full"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                {scanning ? "Checking..." : "Run Integrity Check"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Database Guide Dialog */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-2xl">
              <BookOpen className="w-6 h-6 mr-2 text-accent" />
              Database Utilities Guide
            </DialogTitle>
          </DialogHeader>
          <DatabaseAdminGuide />
        </DialogContent>
      </Dialog>

      {/* Test Accounts Guide Dialog */}
      <Dialog open={showTestAccountsGuide} onOpenChange={setShowTestAccountsGuide}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <TestAccountsGuide />
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Admin Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-soft hover:text-ink-muted"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-soft hover:text-ink-muted"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-soft hover:text-ink-muted"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-app border-l-4 border-line p-3">
              <p className="text-sm text-accent-deep">
                Password must be at least 8 characters long.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowPasswordDialog(false);
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                disabled={changingPassword}
                className="flex-1 bg-accent-deep hover:bg-accent-deep"
              >
                {changingPassword ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Test Account Dialog */}
      <Dialog open={showCreateTestDialog} onOpenChange={setShowCreateTestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>🧪 Create Test Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Name
              </label>
              <input
                type="text"
                value={testAccountName}
                onChange={(e) => setTestAccountName(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="Test Player"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Email
              </label>
              <input
                type="email"
                value={testAccountEmail}
                onChange={(e) => setTestAccountEmail(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                placeholder="test-player@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={testAccountPassword}
                  onChange={(e) => setTestAccountPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  placeholder="Min 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-soft hover:text-ink-muted"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-ink-soft mt-1">
                👁️ Click the eye icon to show/hide password while typing
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="testAccountIsAdmin"
                checked={testAccountIsAdmin}
                onChange={(e) => setTestAccountIsAdmin(e.target.checked)}
                className="w-4 h-4 text-accent"
              />
              <label htmlFor="testAccountIsAdmin" className="text-sm text-ink-muted">
                Make this an admin account
              </label>
            </div>
            
            <div className="bg-accent-tint-2 border-l-4 border-accent p-3">
              <p className="text-sm text-ink">
                Test accounts can be reset (data deleted) or permanently deleted from the management panel.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowCreateTestDialog(false);
                  setTestAccountName("");
                  setTestAccountEmail("");
                  setTestAccountPassword("");
                  setTestAccountIsAdmin(false);
                  setShowPassword(false);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTestAccount}
                disabled={creatingTestAccount}
                className="flex-1 bg-ink hover:bg-ink-hover"
              >
                {creatingTestAccount ? "Creating..." : "Create Test Account"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Test Account Dialog */}
      <Dialog open={showEditTestDialog} onOpenChange={setShowEditTestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>✏️ Edit Test Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Name
              </label>
              <input
                type="text"
                value={testAccountName}
                onChange={(e) => setTestAccountName(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Test Player"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Email
              </label>
              <input
                type="email"
                value={testAccountEmail}
                onChange={(e) => setTestAccountEmail(e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="test-player@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-ink-muted mb-2">
                New Password (leave blank to keep current)
              </label>
              <div className="relative">
                <input
                  type={showEditPassword ? "text" : "password"}
                  value={testAccountPassword}
                  onChange={(e) => setTestAccountPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Min 8 characters (optional)"
                />
                <button
                  type="button"
                  onClick={() => setShowEditPassword(!showEditPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-soft hover:text-ink-muted"
                >
                  {showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-ink-soft mt-1">
                👁️ Click the eye icon to show/hide password | Only enter if you want to change it
              </p>
            </div>
            
            <div className="bg-app border-l-4 border-line p-3">
              <p className="text-sm text-accent-deep">
                💡 Tip: Use this to fix typos in passwords or update account details
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowEditTestDialog(false);
                  setEditingAccount(null);
                  setTestAccountName("");
                  setTestAccountEmail("");
                  setTestAccountPassword("");
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateTestAccount}
                disabled={updatingTestAccount}
                className="flex-1 bg-ink hover:bg-blue-700"
              >
                {updatingTestAccount ? "Updating..." : "Update Account"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent className="sm:max-w-md" data-testid="test-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-accent-deep" />
              Send test email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-ink-muted">
              Sends a small end-to-end test through the same SMTP path the app uses for real deliveries. The result is
              recorded in Email Activity below.
            </p>
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">Recipient email</label>
              <Input
                type="email"
                autoFocus
                value={testEmailRecipient}
                onChange={(ev) => setTestEmailRecipient(ev.target.value)}
                onKeyDown={(ev) => { if (ev.key === "Enter") handleSendTestEmail(); }}
                placeholder="you@example.com"
                data-testid="test-email-recipient-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowTestEmailDialog(false); setTestEmailRecipient(""); }}
                data-testid="test-email-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                data-testid="test-email-send-btn"
                className="bg-accent-deep hover:bg-accent-deep"
              >
                {sendingTestEmail ? (
                  <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />Sending…</>
                ) : (
                  <><Send className="w-3.5 h-3.5 mr-1" />Send test</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Detail Dialog */}
      <Dialog open={!!showEmailDetail} onOpenChange={(o) => { if (!o) setShowEmailDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="email-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              {showEmailDetail?.status === "sent" ? (
                <CheckCircle className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
              )}
              <span className="truncate">{showEmailDetail?.subject}</span>
            </DialogTitle>
          </DialogHeader>
          {showEmailDetail && (
            <div className="space-y-3 py-1 text-sm">
              <div className="grid grid-cols-[100px_1fr] gap-y-1.5 gap-x-3">
                <div className="text-ink-muted">To</div>
                <div className="text-ink break-all">{(showEmailDetail.recipients || []).join(", ")}</div>
                <div className="text-ink-muted">When</div>
                <div className="text-ink">{new Date(showEmailDetail.timestamp).toLocaleString("en-GB")}</div>
                <div className="text-ink-muted">Category</div>
                <div className="text-ink">{showEmailDetail.category}</div>
                <div className="text-ink-muted">Status</div>
                <div className={showEmailDetail.status === "sent" ? "text-emerald-700 font-medium" : "text-red-700 font-medium"}>
                  {showEmailDetail.status}
                </div>
                <div className="text-ink-muted">Triggered by</div>
                <div className="text-ink">{showEmailDetail.triggered_by || "system"}</div>
                {showEmailDetail.has_attachments && (
                  <>
                    <div className="text-ink-muted">Attachments</div>
                    <div className="text-ink">Yes (not preserved for resend)</div>
                  </>
                )}
              </div>
              {showEmailDetail.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-xs break-words">
                  <strong>Error:</strong> {showEmailDetail.error}
                </div>
              )}
              {showEmailDetail.html_content ? (
                <div>
                  <p className="text-xs font-medium text-ink-muted mb-1">Body (HTML preview)</p>
                  <div
                    className="border rounded-md p-3 bg-app max-h-[300px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: showEmailDetail.html_content }}
                  />
                </div>
              ) : showEmailDetail.text_content ? (
                <div>
                  <p className="text-xs font-medium text-ink-muted mb-1">Body (text)</p>
                  <pre className="border rounded-md p-3 bg-app max-h-[300px] overflow-y-auto whitespace-pre-wrap font-sans text-ink text-xs">
                    {showEmailDetail.text_content}
                  </pre>
                </div>
              ) : (
                <p className="text-xs text-ink-muted italic">Body not stored (early log entry).</p>
              )}
              <div className="flex gap-2 justify-end pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailDetail(null)}
                  data-testid="email-detail-close-btn"
                >
                  Close
                </Button>
                {(showEmailDetail.html_content || showEmailDetail.text_content) && (
                  <Button
                    onClick={() => { handleResendEmail(showEmailDetail); setShowEmailDetail(null); }}
                    disabled={resendingId === showEmailDetail.id}
                    data-testid="email-detail-resend-btn"
                    className="bg-accent-deep hover:bg-accent-deep"
                  >
                    <Send className="w-3.5 h-3.5 mr-1" />
                    Resend
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
